/* ─────────────────────────────────────────────────────────────
   SmartFinance · Conferência de fatura (PDF do banco × lançamentos do app)
   Funções puras, sem React.

   Cruza cada linha da fatura do banco com os lançamentos do cartão naquele mês de
   vencimento, usando principalmente VALOR e DATA (descrição só desempata):
     1. mesmo valor e data a até 3 dias                  → bate
     2. mesmo valor e mesma parcela ("03/10" na descrição) → bate
     3. mesmo valor, data diferente                      → bate com data diferente
     4. data a até 3 dias e descrição parecida (ou mesma parcela), valor diferente → valor errado
     5. mesmo dia, um de cada lado sobrando              → valor errado (pela data)
   O que sobra do banco falta no app; o que sobra do app não está na fatura.
   Diferença total = faltas − sobras + Σ(valor do banco − valor do app). Sempre fecha.
───────────────────────────────────────────────────────────── */
import { RE_PAGTO_NO_CARTAO, chaveDescricao } from "./importBanco";

export const cent = v => Math.round((Number(v) || 0) * 100);
const diasEntre = (a, b) => (a && b ? Math.abs(Date.parse(`${a}T12:00:00`) - Date.parse(`${b}T12:00:00`)) / 864e5 : 999);
const tokens = s => new Set(chaveDescricao(s || "").split(" ").filter(t => t.length >= 3));
export const similaridade = (a, b) => {
  const A = tokens(a), B = tokens(b);
  if (!A.size || !B.size) return 0;
  let inter = 0;
  A.forEach(t => { if (B.has(t) || [...B].some(u => u.length >= 4 && t.length >= 4 && (u.startsWith(t) || t.startsWith(u)))) inter++; });
  return inter / Math.min(A.size, B.size);
};
// "LOJA X 03/10", "PARC 3 DE 10", "3/10": parcela 3 de 10 (ignora o que parece data dd/mm/aaaa)
export const parcelaNoTexto = s => {
  const t = String(s || "");
  const m = t.match(/(?:^|[^\d/])(\d{1,2})\s?(?:\/|de)\s?(\d{1,2})(?![\d/])/i);
  if (!m) return null;
  const p = +m[1], tot = +m[2];
  return p >= 1 && tot >= 2 && tot <= 48 && p <= tot ? { p, tot } : null;
};
const mesmaParcela = (b, a) => !!(b.parcela && a.totalParcelas > 1 && b.parcela.p === a.parcela && b.parcela.tot === a.totalParcelas);
const descApp = a => a.descricao || a.contaNome || "";

/* banco: movs lidos do PDF (lerArquivoBanco com destino cartão: compra < 0, crédito > 0)
   app:   lançamentos do cartão com vencimentoMK da fatura (valor > 0 = gasto, < 0 = crédito) */
export function conferirFatura({ banco, app }) {
  const B = (banco || [])
    .filter(m => !(m.valor > 0 && RE_PAGTO_NO_CARTAO.test(m.descBanco || m.descricao || ""))) // pagamento da fatura anterior não é lançamento
    .map((m, i) => ({ i, data: m.data, desc: m.descricao || m.descBanco || "", descBanco: m.descBanco || "", c: -cent(m.valor), parcela: parcelaNoTexto(m.descBanco || m.descricao) }));
  const A = (app || []).map((l, i) => ({ i, l, data: l.data, desc: descApp(l), c: cent(l.valor), parcela: l.parcela, totalParcelas: l.totalParcelas }));
  const usadoB = new Set(), usadoA = new Set();
  const itens = [];

  const passo = (aceita, pontua, status, nota) => {
    const pares = [];
    B.forEach(b => { if (usadoB.has(b.i)) return; A.forEach(a => { if (usadoA.has(a.i) || !aceita(b, a)) return; pares.push({ b, a, s: pontua(b, a) }); }); });
    pares.sort((x, y) => y.s - x.s);
    pares.forEach(({ b, a }) => {
      if (usadoB.has(b.i) || usadoA.has(a.i)) return;
      usadoB.add(b.i); usadoA.add(a.i);
      itens.push({ status, b, a, dif: b.c - a.c, nota: nota ? nota(b, a) : "" });
    });
  };
  const sim = (b, a) => similaridade(b.desc + " " + b.descBanco, a.desc);
  // 1) valor igual e data próxima
  passo((b, a) => b.c === a.c && diasEntre(b.data, a.data) <= 3, (b, a) => 10 - diasEntre(b.data, a.data) + sim(b, a), "ok");
  // 2) valor igual e mesma parcela (banco costuma mostrar a data da compra original)
  passo((b, a) => b.c === a.c && mesmaParcela(b, a), (b, a) => sim(b, a) - diasEntre(b.data, a.data) / 1000, "ok");
  // 3) valor igual, data diferente
  passo((b, a) => b.c === a.c, (b, a) => sim(b, a) * 100 - diasEntre(b.data, a.data), "data",
    (b, a) => `data no banco ${br(b.data)}, no app ${br(a.data)}`);
  // 4) valor diferente: data próxima e descrição parecida (ou mesma parcela)
  passo((b, a) => (diasEntre(b.data, a.data) <= 3 && sim(b, a) >= 0.5) || mesmaParcela(b, a),
    (b, a) => sim(b, a) * 10 - diasEntre(b.data, a.data) - Math.abs(b.c - a.c) / 1e6, "valor");
  // 5) valor diferente: mesmo dia, um de cada lado sobrando
  const sobraB = B.filter(b => !usadoB.has(b.i)), sobraA = A.filter(a => !usadoA.has(a.i));
  const porDia = {};
  sobraB.forEach(b => { (porDia[b.data] = porDia[b.data] || { b: [], a: [] }).b.push(b); });
  sobraA.forEach(a => { if (porDia[a.data]) porDia[a.data].a.push(a); });
  Object.values(porDia).forEach(g => {
    if (g.b.length === 1 && g.a.length === 1) {
      const [b] = g.b, [a] = g.a;
      usadoB.add(b.i); usadoA.add(a.i);
      itens.push({ status: "valor", b, a, dif: b.c - a.c, nota: "pareado pela data; confira se é a mesma compra" });
    }
  });
  B.filter(b => !usadoB.has(b.i)).forEach(b => itens.push({ status: "falta", b, a: null, dif: b.c }));
  A.filter(a => !usadoA.has(a.i)).forEach(a => itens.push({ status: "sobra", b: null, a, dif: -a.c }));

  const soma = (st, f) => itens.filter(x => st.includes(x.status)).reduce((t, x) => t + f(x), 0);
  const totalBanco = B.reduce((t, b) => t + b.c, 0);
  const totalApp = A.reduce((t, a) => t + a.c, 0);
  const resumo = {
    ok: itens.filter(x => x.status === "ok").length,
    data: itens.filter(x => x.status === "data").length,
    valor: { n: itens.filter(x => x.status === "valor").length, c: soma(["valor"], x => x.dif) },
    falta: { n: itens.filter(x => x.status === "falta").length, c: soma(["falta"], x => x.b.c) },
    sobra: { n: itens.filter(x => x.status === "sobra").length, c: soma(["sobra"], x => x.a.c) },
  };
  const ordem = { valor: 0, falta: 1, sobra: 2, data: 3, ok: 4 };
  itens.sort((x, y) => ordem[x.status] - ordem[y.status] || ((x.b || x.a).data || "").localeCompare((y.b || y.a).data || ""));
  return { itens, totalBanco: totalBanco / 100, totalApp: totalApp / 100, dif: (totalBanco - totalApp) / 100, resumo, datasBanco: B.map(b => b.data).filter(Boolean).sort() };
}
const br = d => (d ? `${d.slice(8, 10)}/${d.slice(5, 7)}` : "?");

/* Onde está o lançamento que falta? Mesmo valor em outra fatura do mesmo cartão ou em outro cartão.
   outros: lançamentos de cartão do perfil fora desta fatura. */
export function localizarFalta(item, outros) {
  const b = item.b;
  const cands = (outros || []).filter(l => cent(l.valor) === b.c && (diasEntre(b.data, l.data) <= 5 || (b.parcela && l.parcela === b.parcela.p && l.totalParcelas === b.parcela.tot)));
  cands.sort((x, y) => diasEntre(b.data, x.data) - diasEntre(b.data, y.data));
  return cands[0] || null;
}
